import { useSetAtom } from 'jotai'
import { useEffect } from 'react'
import { lastWebSocketPingAtom } from '~/atoms/ConnectionState'
import { useWebSocketService } from '~/hooks/useWebSocketService'
import { WebSocketEventType } from '~/types/WebSocketTypes'

const SUBSCRIPTION_TOKEN = 'websocket-heartbeat-liveness'

/**
 * Bridges the server WebSocket heartbeat into a jotai atom so the status panel
 * can show connection liveness. Must run inside the app's JotaiProvider tree
 * (via useSetAtom) — the WebSocketService singleton can't write the atom
 * itself because getDefaultStore() is a different store than the Provider's.
 *
 * Mount once at an always-rendered, post-auth location.
 *
 * The timestamp is intentionally NOT cleared on disconnect: keeping the last
 * heartbeat lets the panel show "last beat Ns ago (stale)" — more useful for
 * support than "no heartbeat" — and the disconnect itself is surfaced
 * separately via connectionStatus. useHeartbeatAgo flags staleness past ~75s.
 */
export function useWebSocketHeartbeat() {
  const setLastPing = useSetAtom(lastWebSocketPingAtom)
  const { subscribe } = useWebSocketService()

  useEffect(() => {
    subscribe(WebSocketEventType.Ping, {
      subscriptionToken: SUBSCRIPTION_TOKEN,
      callback: () => setLastPing(Date.now()),
    })
    // useWebSocketService cleans up this hook's subscriptions on unmount.
  }, [subscribe, setLastPing])
}
